import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import company from "assets/images/icon/company.png";
import companystatus from "assets/images/icon/company-status.png";
import onboard from "assets/images/icon/onboard.png";
import implementation from "assets/images/icon/implementation.png";
import Card from 'components/Card';
import GeneralInformationSection from './GeneralInformationSection';
import WithdrawalBankAccountSection from './WithdrawalBankAccountSection';
import ManageWorkplaces from './ManageWorkplaces';
import ManageDepartments from './ManageDepartments';
import ManagePaySchedules from './ManagePaySchedules';
import ManageEaringCodes from './ManageEarningCodes';
import FederalStateTaxesSection from './FederalStateTaxesSection';
import EmployeeSetupSection from './EmployeeSetupSection';
import FilingAuthorizationSection from './FilingAuthorizationSection';
import AccountingIntegrationSection from './AccountingIntegrationSection';
import WorkersCompensationSection from './WorkersCompensationSection';
import PriorPayrollSection from './PriorPayrollSection';
import { getEmployees } from 'services/employees';
import { getContractors } from 'services/contractors';
import axiosInstance from '../../axios/axiosInstance';
import ENV from 'constants/env';
import { useSetupIndicators } from 'components/CompanySetupChecklist';

const companyId = () => localStorage.getItem('companyId') || ENV.COMPANY_ID;

function ManageCompany() {
    const { data: employees = [] } = useQuery({ queryKey: ['employees'], queryFn: getEmployees });
    const { data: contractors = [] } = useQuery({ queryKey: ['contractors'], queryFn: getContractors });
    const { data: pendingAccounts = [] } = useQuery({
        queryKey: ['pending-approvals'],
        queryFn: async () => {
            const res = await axiosInstance.get(`/auth/pending-approvals/${companyId()}`);
            return Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
        },
    });

    const activeEmployees = employees.filter(e => e.active !== false && e.isActive !== false).length;
    const activeContractors = contractors.filter(c => c.active !== false && c.isActive !== false).length;
    const pendingCount = pendingAccounts.length;
    const totalWorkers = activeEmployees + activeContractors;

    const companyDetail = [
        { title: 'Employees', count: activeEmployees, icon: company },
        { title: 'Contractors', count: activeContractors, icon: companystatus },
        { title: 'Pending Onboards', count: pendingCount, icon: onboard },
        { title: 'Total Workers', count: totalWorkers, icon: implementation }
    ];
    const [activeLink, setActiveLink] = useState("company-information");

    const setupDone = useSetupIndicators();

    const menuItems = [
        {
            title: "Company Details",
            links: [
                { name: "Company Information", path: "company-information", checkId: "company-info" },
                { name: "Withdrawal Bank Account", path: "withdrawal-bank-account", checkId: "bank-account" }
            ]
        },
        {
            title: "Company Management",
            links: [
                { name: "Workplaces",    path: "workplaces",    checkId: "workplaces" },
                { name: "Departments",   path: "departments",   checkId: "departments" },
                { name: "Pay Schedules", path: "pay-schedules", checkId: "pay-schedules" },
                { name: "Earning Codes", path: "earning-codes" }
            ]
        },
        {
            title: "Company Onboarding",
            links: [
                { name: "Federal & State Taxes",  path: "federal-state-taxes",   checkId: "federal-taxes" },
                { name: "Workers Compensation",   path: "workers-compensation",  checkId: "workers-comp" },
                { name: "Prior Payroll",          path: "prior-payroll",         checkId: "prior-payroll" },
                { name: "Employee Setup",         path: "employee-setup",        checkId: "employee-setup" },
                { name: "Filling Authorization",  path: "filling-authorization", checkId: "filing-auth" },
                { name: "Accounting Integration", path: "accounting-integration" }
            ]
        }
    ];
     useEffect(() => {
        const hash = window.location.hash.replace("#", "");
        if (hash) {
            setActiveLink(hash);
        } else {
            setActiveLink("company-information");
        }
    }, []);

    const handleLinkClick = (path) => {
        setActiveLink(path);
        window.location.hash = path;
    };

    const getSectionContent = useMemo(() => {
        switch (activeLink) {
            case "company-information":
                return <GeneralInformationSection />;
            case "withdrawal-bank-account":
                return <WithdrawalBankAccountSection />;
            case "workplaces":
                return <ManageWorkplaces />;
            case "departments":
                return <ManageDepartments />;
            case "pay-schedules":
                return <ManagePaySchedules />;
            case "earning-codes":
                return <ManageEaringCodes />;
            case "federal-state-taxes":
                return <FederalStateTaxesSection />;
            case "workers-compensation":
                return <WorkersCompensationSection />;
            case "prior-payroll":
                return <PriorPayrollSection />;
            case "employee-setup":
                return <EmployeeSetupSection />;
            case "filling-authorization":
                return <FilingAuthorizationSection />;
            case "accounting-integration":
                return <AccountingIntegrationSection />;
            default:
                return <p>Select an option to view details.</p>;
        }
    }, [activeLink]);

    return (
        <div className='content' style={{ padding: "16px 24px 16px 16px" }}>
            <div className="payroll-manage-company-section">
                {/* <div className='breadcrumb-section pb-3 border-bottom'>
                    <h2 className='main-title mb-1'>Payroll</h2>
                    <nav aria-label="breadcrumb">
                        <ol className="breadcrumb">
                            <li className="breadcrumb-item"><Link to="/dashboard">Home</Link></li>
                            <li className="breadcrumb-item"><Link to="/dashboard">Dashboard</Link></li>
                            <li className="breadcrumb-item"><a href="manage-company">Manage Company</a></li>
                        </ol>
                    </nav>
                </div> */}

                <div className="row mt-4">
                    {companyDetail?.map((item) => {
                        return <Card key={item.title} title={item?.title} count={item?.count} icon={item?.icon} />
                    })}
                </div>

                <div className="row pb-4">
                    <div className="col-lg-3">
                        <div className='border-gray cursor-pointer h-100 rounded-3 p-3 mt-4 manage-company-details-section'>
                            {menuItems.map((section, index) => (
                                <div key={index} className={index > 0 ? "mt-4" : ""}>
                                    <h2 className="details-title">{section.title}</h2>
                                    <ul>
                                        {section.links.map((link, idx) => {
                                            const isDone = link.checkId ? setupDone[link.checkId] : null;
                                            return (
                                                <li key={idx} className={activeLink === link.path ? "active" : ""}>
                                                    <a href={`${'#'+link.path}`} onClick={() => handleLinkClick(link.path)}
                                                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                                                        <span>{link.name}</span>
                                                        {link.checkId && (
                                                            <span style={{ fontSize: 13, flexShrink: 0 }}>
                                                                {isDone ? '✅' : '⚠️'}
                                                            </span>
                                                        )}
                                                    </a>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="col-lg-9">
                        <div className='border-gray rounded-3 h-100 p-3 mt-4 manage-company-details-section'>
                            {getSectionContent} 
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}


export default ManageCompany;
